import type { EquipmentFace } from '../../types';
import type { DeviceModel, DevicePortRole, ElectricalGraph, ElectricalGraphCamera, ElectricalGraphPos, ElectricalGraphWireEnds, PowerInput, PowerOutput } from '../../types/deviceModel';
import { defaultPoeIn, pruneAndEnsurePoeInputs } from './electricalDraft';
import type { FacedPort } from './layoutPorts';
import { applyRoleToPort, unapplyRoleFromPort } from './portRoles';

export type GraphPinKind =
  | 'input'
  | 'input-plus'
  | 'input-minus'
  | 'output'
  | 'role'
  | 'port';

export interface GraphPin {
  id: string;
  kind: GraphPinKind;
  cardId?: string;
  face?: EquipmentFace;
  portNumber?: number;
  portKind?: string;
}

export interface GraphEdge {
  id: string;
  from: GraphPin;
  to: GraphPin;
}

export interface GraphConnectResult {
  model: DeviceModel;
  ports: FacedPort[];
  ok: boolean;
  reason?: string;
}

export function portPinId(face: string, number: number, kind: string): string {
  return `port:${face}:${number}:${kind}`;
}

export function parsePortPinId(id: string): { face: EquipmentFace; number: number; kind: string } | null {
  if (!id.startsWith('port:')) return null;
  const rest = id.slice(5);
  const parts = rest.split(':');
  if (parts.length < 3) return null;
  const kind = parts.slice(2).join(':');
  const number = parseInt(parts[1], 10);
  if (!Number.isFinite(number)) return null;
  return { face: parts[0] as EquipmentFace, number, kind };
}

export function inputPinId(inputId: string, pole: 'out' | 'plus' | 'minus' = 'out'): string {
  return pole === 'out' ? `input:${inputId}` : `input:${inputId}:${pole}`;
}

export function outputPinId(outputId: string): string {
  return `output:${outputId}`;
}

export function rolePinId(roleId: string): string {
  return `role:${roleId}`;
}

export function parseCardPinId(id: string): GraphPin | null {
  if (id.startsWith('role:')) return { id, kind: 'role', cardId: id.slice(5) };
  if (id.startsWith('output:')) return { id, kind: 'output', cardId: id.slice(7) };
  if (id.startsWith('input:')) {
    const rest = id.slice(6);
    if (rest.endsWith(':plus')) {
      return { id, kind: 'input-plus', cardId: rest.slice(0, -5) };
    }
    if (rest.endsWith(':minus')) {
      return { id, kind: 'input-minus', cardId: rest.slice(0, -6) };
    }
    return { id, kind: 'input', cardId: rest };
  }
  const port = parsePortPinId(id);
  if (port) return { id, kind: 'port', face: port.face, portNumber: port.number, portKind: port.kind };
  return null;
}

function findPort(ports: FacedPort[], face: string, number: number, kind?: string): FacedPort | undefined {
  return ports.find(
    (p) =>
      (p.face || 'front') === face &&
      p.number === number &&
      (!kind || String(p.kind) === kind)
  );
}

export function pinsCompatible(a: GraphPin, b: GraphPin, model: DeviceModel): boolean {
  const port = a.kind === 'port' ? a : b.kind === 'port' ? b : null;
  const card = a.kind === 'port' ? b : a;
  if (!port || card.kind === 'port') return false;
  const pk = String(port.portKind || '');
  if (card.kind === 'role') {
    const role = (model.portRoles || []).find((r) => r.id === card.cardId);
    return !!role && pk === 'rj45';
  }
  if (card.kind === 'output') {
    const out = (model.powerOutputs || []).find((o) => o.id === card.cardId);
    if (!out) return false;
    return out.kind === 'ac' ? pk === 'ac' : pk === 'dc';
  }
  if (card.kind === 'input-plus') {
    const input = model.powerInputs.find((i) => i.id === card.cardId);
    if (!input) return false;
    if (input.kind === 'pv') return pk === 'pvPlus';
    if (input.kind === 'battery') return pk === 'batPlus';
    return false;
  }
  if (card.kind === 'input-minus') {
    const input = model.powerInputs.find((i) => i.id === card.cardId);
    if (!input) return false;
    if (input.kind === 'pv') return pk === 'pvMinus';
    if (input.kind === 'battery') return pk === 'batMinus';
    return false;
  }
  if (card.kind === 'input') {
    const input = model.powerInputs.find((i) => i.id === card.cardId);
    if (!input) return false;
    if (input.kind === 'poe-in') return pk === 'rj45';
    if (input.kind === 'ac') return pk === 'ac';
    if (input.kind === 'dc-jack' || input.kind === 'dc-terminal') return pk === 'dc';
    return false;
  }
  return false;
}

export function edgesFromModel(model: DeviceModel, ports: FacedPort[]): GraphEdge[] {
  const edges: GraphEdge[] = [];
  (model.portRoles || []).forEach((role) => {
    ports.forEach((port) => {
      const match =
        (role.kind === 'poe-in' && port.poeInRoleId === role.id) ||
        (role.kind === 'poe-out' && port.poeOutRoleId === role.id);
      if (!match) return;
      const to: GraphPin = {
        id: portPinId(port.face || 'front', port.number, String(port.kind)),
        kind: 'port',
        face: port.face,
        portNumber: port.number,
        portKind: String(port.kind),
      };
      const from: GraphPin = { id: rolePinId(role.id), kind: 'role', cardId: role.id };
      edges.push({ id: `${from.id}->${to.id}`, from, to });
    });
  });
  model.powerInputs.forEach((input) => {
    if (input.kind === 'pv' || input.kind === 'battery') {
      const plusKind = input.kind === 'pv' ? 'pvPlus' : 'batPlus';
      const minusKind = input.kind === 'pv' ? 'pvMinus' : 'batMinus';
      if (input.plusPortNumber != null) {
        const face = (input.plusFace || 'front') as EquipmentFace;
        const to: GraphPin = {
          id: portPinId(face, input.plusPortNumber, plusKind),
          kind: 'port',
          face,
          portNumber: input.plusPortNumber,
          portKind: plusKind,
        };
        const from: GraphPin = { id: inputPinId(input.id, 'plus'), kind: 'input-plus', cardId: input.id };
        edges.push({ id: `${from.id}->${to.id}`, from, to });
      }
      if (input.minusPortNumber != null) {
        const face = (input.minusFace || 'front') as EquipmentFace;
        const to: GraphPin = {
          id: portPinId(face, input.minusPortNumber, minusKind),
          kind: 'port',
          face,
          portNumber: input.minusPortNumber,
          portKind: minusKind,
        };
        const from: GraphPin = { id: inputPinId(input.id, 'minus'), kind: 'input-minus', cardId: input.id };
        edges.push({ id: `${from.id}->${to.id}`, from, to });
      }
      return;
    }
    if (input.portNumber == null) return;
    const face = (input.face || 'front') as EquipmentFace;
    const port =
      findPort(ports, face, input.portNumber) ||
      ports.find((p) => p.number === input.portNumber && (p.face || 'front') === face);
    if (!port) return;
    if (input.kind === 'poe-in' && port.poeInRoleId) return;
    const to: GraphPin = {
      id: portPinId(port.face || 'front', port.number, String(port.kind)),
      kind: 'port',
      face: port.face,
      portNumber: port.number,
      portKind: String(port.kind),
    };
    const from: GraphPin = { id: inputPinId(input.id), kind: 'input', cardId: input.id };
    edges.push({ id: `${from.id}->${to.id}`, from, to });
  });
  (model.powerOutputs || []).forEach((output) => {
    if (output.portNumber == null) return;
    const face = (output.face || 'front') as EquipmentFace;
    const wantKind = output.kind === 'ac' ? 'ac' : 'dc';
    const port =
      findPort(ports, face, output.portNumber, wantKind) ||
      findPort(ports, face, output.portNumber);
    if (!port) return;
    const to: GraphPin = {
      id: portPinId(port.face || 'front', port.number, String(port.kind)),
      kind: 'port',
      face: port.face,
      portNumber: port.number,
      portKind: String(port.kind),
    };
    const from: GraphPin = { id: outputPinId(output.id), kind: 'output', cardId: output.id };
    edges.push({ id: `${from.id}->${to.id}`, from, to });
  });
  return edges;
}

function findConnectTarget(ports: FacedPort[], face: string, number: number, kind?: string): number {
  const strict = ports.findIndex(
    (p) => (p.face || 'front') === face && p.number === number && (!kind || String(p.kind) === kind)
  );
  if (strict >= 0) return strict;
  return ports.findIndex((p) => (p.face || 'front') === face && p.number === number);
}

function splitPins(fromId: string, toId: string): { card: GraphPin; port: GraphPin } | null {
  const a = parseCardPinId(fromId);
  const b = parseCardPinId(toId);
  if (!a || !b) return null;
  if (a.kind === 'port' && b.kind !== 'port') return { card: b, port: a };
  if (b.kind === 'port' && a.kind !== 'port') return { card: a, port: b };
  return null;
}

export function connectPin(model: DeviceModel, ports: FacedPort[], fromId: string, toId: string): GraphConnectResult {
  const split = splitPins(fromId, toId);
  if (!split) return { model, ports, ok: false, reason: 'Need a card pin and a port pin.' };
  if (!pinsCompatible(split.card, split.port, model)) {
    return { model, ports, ok: false, reason: 'Those pins are not compatible.' };
  }
  const { card, port } = split;
  const face = port.face || 'front';
  const number = port.portNumber!;
  const kind = port.portKind;

  if (card.kind === 'role' && card.cardId) {
    const role = (model.portRoles || []).find((r) => r.id === card.cardId);
    if (!role) return { model, ports, ok: false, reason: 'Missing role.' };
    const idx = findConnectTarget(ports, face, number, kind);
    if (idx < 0) return { model, ports, ok: false, reason: 'No matching port.' };
    const nextPorts = ports.map((p, i) => (i === idx ? applyRoleToPort(p, role) : p));
    return { model, ports: nextPorts, ok: true };
  }

  if (card.kind === 'output' && card.cardId) {
    const powerOutputs = (model.powerOutputs || []).map((o) =>
      o.id === card.cardId ? { ...o, portNumber: number, face } : o
    );
    return { model: { ...model, powerOutputs }, ports, ok: true };
  }

  if ((card.kind === 'input-plus' || card.kind === 'input-minus') && card.cardId) {
    const input = model.powerInputs.find((i) => i.id === card.cardId);
    if (!input) return { model, ports, ok: false, reason: 'Missing input.' };
    if (card.kind === 'input-plus' && kind !== 'pvPlus' && kind !== 'batPlus') {
      return { model, ports, ok: false, reason: 'Plus pin must land on a + port.' };
    }
    if (card.kind === 'input-minus' && kind !== 'pvMinus' && kind !== 'batMinus') {
      return { model, ports, ok: false, reason: 'Minus pin must land on a − port.' };
    }
    const powerInputs = model.powerInputs.map((i) => {
      if (i.id !== card.cardId) return i;
      if (card.kind === 'input-plus') return { ...i, plusPortNumber: number, plusFace: face };
      return { ...i, minusPortNumber: number, minusFace: face };
    });
    return { model: { ...model, powerInputs }, ports, ok: true };
  }

  if (card.kind === 'input' && card.cardId) {
    const input = model.powerInputs.find((i) => i.id === card.cardId);
    if (!input) return { model, ports, ok: false, reason: 'Missing input.' };
    const powerInputs = model.powerInputs.map((i) =>
      i.id === card.cardId ? { ...i, portNumber: number, face } : i
    );
    let nextPorts = ports;
    if (input.kind === 'poe-in') {
      nextPorts = ports.map((p) => {
        if ((p.face || 'front') !== face || p.number !== number) return p;
        if (p.poeIn) return p;
        return { ...p, poeIn: defaultPoeIn() };
      });
    }
    return { model: { ...model, powerInputs }, ports: nextPorts, ok: true };
  }

  return { model, ports, ok: false, reason: 'Cannot connect those pins.' };
}

export function disconnectPin(model: DeviceModel, ports: FacedPort[], fromId: string, toId: string): GraphConnectResult {
  const split = splitPins(fromId, toId);
  if (!split) return { model, ports, ok: false };
  const { card, port } = split;
  const face = port.face || 'front';
  const number = port.portNumber!;
  const kind = port.portKind;

  if (card.kind === 'role' && card.cardId) {
    const role = (model.portRoles || []).find((r) => r.id === card.cardId);
    if (!role) return { model, ports, ok: false };
    const nextPorts = ports.map((p) => {
      if ((p.face || 'front') !== face || p.number !== number || String(p.kind) !== kind) return p;
      return unapplyRoleFromPort(p, role);
    });
    return { model, ports: nextPorts, ok: true };
  }

  if (card.kind === 'output' && card.cardId) {
    const powerOutputs = (model.powerOutputs || []).map((o) =>
      o.id === card.cardId ? { ...o, portNumber: undefined, face: undefined } : o
    );
    return { model: { ...model, powerOutputs }, ports, ok: true };
  }

  if (card.kind === 'input-plus' && card.cardId) {
    const powerInputs = model.powerInputs.map((i) =>
      i.id === card.cardId ? { ...i, plusPortNumber: undefined, plusFace: undefined } : i
    );
    return { model: { ...model, powerInputs }, ports, ok: true };
  }
  if (card.kind === 'input-minus' && card.cardId) {
    const powerInputs = model.powerInputs.map((i) =>
      i.id === card.cardId ? { ...i, minusPortNumber: undefined, minusFace: undefined } : i
    );
    return { model: { ...model, powerInputs }, ports, ok: true };
  }
  if (card.kind === 'input' && card.cardId) {
    const powerInputs = model.powerInputs.map((i) =>
      i.id === card.cardId ? { ...i, portNumber: undefined, face: undefined } : i
    );
    return { model: { ...model, powerInputs }, ports, ok: true };
  }
  return { model, ports, ok: false };
}

export function cardIsLive(model: DeviceModel, ports: FacedPort[], cardId: string): boolean {
  const edges = edgesFromModel(model, ports);
  const wired = edges.some((e) => e.from.cardId === cardId);
  const input = model.powerInputs.find((i) => i.id === cardId);
  if (input) return input.enabled !== false && wired;
  const output = (model.powerOutputs || []).find((o) => o.id === cardId);
  if (output) return output.enabled !== false && wired;
  const role = (model.portRoles || []).find((r) => r.id === cardId);
  if (role) return role.enabled !== false && wired;
  return false;
}

export function autoLayoutCards(model: DeviceModel): Record<string, { x: number; y: number }> {
  const cards: Record<string, { x: number; y: number }> = {};
  model.powerInputs.forEach((input, i) => {
    cards[input.id] = { x: 40, y: 40 + i * 96 };
  });
  (model.portRoles || []).forEach((role, i) => {
    cards[role.id] = { x: 40, y: 40 + (model.powerInputs.length + i) * 96 };
  });
  (model.powerOutputs || []).forEach((output, i) => {
    cards[output.id] = { x: 720, y: 40 + i * 96 };
  });
  return cards;
}

export function mergeGraphPositions(model: DeviceModel, patch: Partial<ElectricalGraph>): DeviceModel {
  const prev = model.electricalGraph || {};
  const wireEnds = { ...(prev.wireEnds || {}) };
  if (patch.wireEnds) {
    Object.entries(patch.wireEnds).forEach(([id, ends]) => {
      if (!ends) {
        delete wireEnds[id];
        return;
      }
      wireEnds[id] = { ...wireEnds[id], ...ends };
    });
  }
  return {
    ...model,
    electricalGraph: {
      camera: patch.camera ?? prev.camera,
      boards: { ...(prev.boards || {}), ...(patch.boards || {}) },
      cards: { ...(prev.cards || {}), ...(patch.cards || {}) },
      wireEnds,
    },
  };
}

export function pruneStaleWireEnds(model: DeviceModel, ports: FacedPort[]): DeviceModel {
  const prev = model.electricalGraph?.wireEnds;
  if (!prev) return model;
  const live = new Set(edgesFromModel(model, ports).map((e) => e.id));
  let changed = false;
  const wireEnds: Record<string, ElectricalGraphWireEnds> = {};
  Object.entries(prev).forEach(([id, ends]) => {
    if (!live.has(id)) {
      changed = true;
      return;
    }
    wireEnds[id] = ends;
  });
  if (!changed) return model;
  return {
    ...model,
    electricalGraph: { ...(model.electricalGraph || {}), wireEnds },
  };
}

export function snapshotElectricalGraph(
  model: DeviceModel,
  live: {
    camera: ElectricalGraphCamera;
    cards?: Record<string, ElectricalGraphPos>;
    board?: ElectricalGraphPos | null;
    face?: EquipmentFace;
  }
): DeviceModel {
  return mergeGraphPositions(model, {
    camera: live.camera,
    cards: live.cards,
    boards: live.board && live.face ? { [live.face]: live.board } : undefined,
  });
}

export function stripDisabledRolePoe(ports: FacedPort[], roles: DevicePortRole[] | undefined): FacedPort[] {
  const disabled = new Set((roles || []).filter((r) => r.enabled === false).map((r) => r.id));
  if (!disabled.size) return ports;
  return ports.map((p) => {
    let next: FacedPort = { ...p };
    if (p.poeInRoleId && disabled.has(p.poeInRoleId)) {
      const { poeIn: _in, ...rest } = next;
      next = rest as FacedPort;
    }
    if (p.poeOutRoleId && disabled.has(p.poeOutRoleId)) {
      const { poeOut: _out, ...rest } = next;
      next = rest as FacedPort;
    }
    return next;
  });
}

export function deleteElectricalCard(model: DeviceModel, ports: FacedPort[], cardId: string): GraphConnectResult {
  const role = (model.portRoles || []).find((r) => r.id === cardId);
  if (role) {
    let nextPorts = ports;
    ports.forEach((p) => {
      nextPorts = nextPorts.map((x) => unapplyRoleFromPort(x, role));
    });
    return {
      model: {
        ...model,
        portRoles: (model.portRoles || []).filter((r) => r.id !== cardId),
        powerInputs: role.kind === 'poe-in' ? pruneAndEnsurePoeInputs(nextPorts, model.powerInputs) : model.powerInputs,
      },
      ports: nextPorts,
      ok: true,
    };
  }
  if (model.powerInputs.some((i) => i.id === cardId)) {
    return {
      model: { ...model, powerInputs: model.powerInputs.filter((i) => i.id !== cardId) },
      ports,
      ok: true,
    };
  }
  if ((model.powerOutputs || []).some((o) => o.id === cardId)) {
    return {
      model: { ...model, powerOutputs: (model.powerOutputs || []).filter((o) => o.id !== cardId) },
      ports,
      ok: true,
    };
  }
  return { model, ports, ok: false };
}

export function cardSummary(input?: PowerInput, output?: PowerOutput, role?: DevicePortRole): string {
  if (role) {
    if (role.kind === 'poe-out' && role.poeOut) {
      return `PoE-out ${role.poeOut.standards.join('/')} · ${role.poeOut.perPortMaxWatts} W`;
    }
    if (role.kind === 'poe-in' && role.poeIn) {
      return `PoE-in ${role.poeIn.standards.join('/')} · ${role.poeIn.voltageMin}–${role.poeIn.voltageMax} V`;
    }
    return role.name;
  }
  if (output) {
    const v = output.voltageMin != null ? `${output.voltageMin} V` : output.kind.toUpperCase();
    return `${output.kind.toUpperCase()} out ${output.maxWatts != null ? `${output.maxWatts} W` : v}`;
  }
  if (input) {
    if (input.kind === 'pv') return `PV/MPPT ${input.mpptVoltageMin ?? '?'}–${input.mpptVoltageMax ?? '?'} V`;
    if (input.kind === 'battery') return `Battery ${input.voltageMin ?? '?'}–${input.voltageMax ?? '?'} V`;
    if (input.kind === 'poe-in') return `PoE-in ${input.voltageMin ?? ''}–${input.voltageMax ?? ''} V`;
    if (input.kind === 'ac') return `AC in ${input.voltageMin ?? 100}–${input.voltageMax ?? 240} V`;
    return `DC in ${input.voltageMin ?? ''}–${input.voltageMax ?? ''} V`;
  }
  return '';
}
